"use client";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { useState } from "react";
import { makeQueryClient } from "./trpc.queryClient";
import type { AppRouter } from "../server/routers/_app";
import superjson from "superjson";
import { errHandler, errHandlerWithoutUserToast } from "./clientLogger";
import { observable, tap } from "@trpc/server/observable";
import { trpcStandalone } from "./trpcStandalone";

function getBaseUrl() {
  if (typeof window !== "undefined")
    // browser should use relative path
    return "";

  if (process.env.VERCEL_URL)
    // reference for vercel.com
    return `https://${process.env.VERCEL_URL}`;

  if (process.env.RENDER_INTERNAL_HOSTNAME)
    // reference for render.com
    return `http://${process.env.RENDER_INTERNAL_HOSTNAME}:${process.env.PORT ?? ""}`;

  // assume localhost
  return `http://localhost:${process.env.PORT ?? String(3000)}`;
}

export const trpc = createTRPCReact<AppRouter>({
  overrides: {
    useMutation: {
      /**
       * This function is called whenever a `.useMutation` succeeds
       **/
      async onSuccess(opts) {
        /**
         * @note that order here matters:
         * The order here allows route changes in `onSuccess` without
         * having a flash of content change whilst redirecting.
         **/
        // Calls the `onSuccess` defined in the `useQuery()` options:
        await opts.originalFn();
        // Invalidate all queries in the react-query cache unless the user explicitly opts out with meta:
        if (opts.meta.skipInvalidate) {
          return;
        }
        opts.queryClient
          .invalidateQueries()
          .catch(errHandler("refreshing queries"));
      },
    },
  },
});

let clientQueryClientSingleton: QueryClient | undefined;

function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: use singleton pattern to keep the same query client
  return (clientQueryClientSingleton ??= makeQueryClient());
}

export function TRPCProvider(
  props: Readonly<{
    children: React.ReactNode;
  }>,
) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        // a logger link that logs the request information if the request is not successful
        () => {
          return ({ op, next }) => {
            const startTime = Date.now();
            return observable((observer) => {
              return next(op)
                .pipe(
                  tap({
                    error(result) {
                      const networkInfo = {
                        online: navigator.onLine,
                        timeSinceStart: Date.now() - startTime,
                      };

                      // Check if it's a network error
                      const isNetworkError =
                        result instanceof Error &&
                        (result.message === "Failed to fetch" ||
                          result.message === "Network request failed");

                      // If it's a network error, don't log it, it's likely a temporary issue
                      if (isNetworkError) {
                        return;
                      }

                      trpcStandalone.clientLog.log
                        .mutate({
                          message: `TRPC client error: ${JSON.stringify(result)}`,
                          createdAt: new Date().toISOString(),
                          url: window.location.href,
                          level: "error",
                          meta: {
                            result: {
                              message: result.message,
                              name: result.name,
                              stack: result.stack,
                              cause: {
                                name: result.cause?.name,
                                message: result.cause?.message,
                                stack: result.cause?.stack,
                              },
                              data: result.data,
                              meta: result.meta,
                              shape: result.shape,
                            },
                            op,
                            networkDiagnostics: {
                              ...networkInfo,
                              // Add response status if available
                              httpStatus: result.data?.httpStatus,
                            },
                          },
                          buildTime:
                            (import.meta.env.VITE_BUILD_TIME as
                              | string
                              | undefined) ?? "unknown",
                        })
                        .catch(
                          errHandlerWithoutUserToast("logging client error"),
                        );
                    },
                  }),
                )
                .subscribe(observer);
            });
          };
        },
        httpBatchLink({
          transformer: superjson,
          url: `${getBaseUrl()}/api/trpc`,
        }),
      ],
    }),
  );
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {props.children}
      </QueryClientProvider>
    </trpc.Provider>
  );
}
